import { ClickThroughNavigation } from '@cbsinteractive/avia-js-gam';
import { APIStreamType } from 'library/ts/enums/metadata/api/APIStreamType';
import { MPXAssetType } from 'library/ts/model/enums/mpx/MPXAssetType';
import { PlayerAdapterVideoFormat } from 'library/ts/player/adapter/enums/PlayerAdapterVideoFormat';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { PlayerLoadingTimeout } from 'tag/ts/tag/model/type/PlayerLoadingTimeout';
import { AbstractDesktopResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/abstract/AbstractDesktopResourceConfigBuilder';
import { PIDPlaybackUtility } from 'tag/ts/tag/player/adapters/avia/utilities/PIDPlaybackUtility';
export class DesktopPIDResourceConfigBuilder extends AbstractDesktopResourceConfigBuilder {
    constructor(argument) {
        super({
            name: 'DesktopPIDResourceConfigBuilder',
            ...argument
        });
        this.assetPlaybackUtil = new PIDPlaybackUtility(argument);
    }
    getResult() {
        return new Promise((resolve, reject) => {
            super.getResult().then((data) => {
                let resource = data?.resource;
                resource = this.setMediaUrl(resource);
                resource = this.setDrm(resource);
                resource = this.setStartTime(resource);
                resource = this.setThumbnailTrackUrl(resource);
                resource = this.setAdParameters(resource);
                resource = this.deepMergeResourceFromSmartTagParams(resource);
                resource = this.removeSsaiWhenAdsDisabledForIntl(resource);
                resource = this.sanitizeResource(resource);
                resource = this.testOverrides(resource);
                resolve({ msg: 'Resource config', resource: resource });
            });
        });
    }
    removeSsaiWhenAdsDisabledForIntl(resource) {
        if (this.tag.tagUtilities.isInternationalContext(this.tag) &&
            this.params?.AD?.VIDEOADS?.enabled === false) {
            resource = this.removeSsai(resource);
            this?.logger?.log('removeSsaiWhenAdsDisabledForIntl', 'region is INTL, params.AD.VIDEOADS.enabled=false... removed ssai params from resource to fallback to playback via mediaUrl');
        }
        return resource;
    }
    setThumbnailTrackUrl(resource) {
        const url = this.apiMetadata.thumbnailSet?.find((item) => item.assetType === APIStreamType.VTT_SPRITE)?.url || undefined;
        if (url) {
            resource = super.setThumbnailTrackUrl(resource, url);
        }
        else {
            this?.logger?.warn('setThumbnailTrackUrl', 'could not find thumbnail url in apiMetadata.thumbnailSet... returning resource unmodified');
        }
        return resource;
    }
    setMediaUrl(resource) {
        return super.setMediaUrl(resource, this.assetPlaybackUtil?.getMediaUrl());
    }
    setViewabilityTracking(resource) {
        const trackingObj = {
            partner: this.tag.model.contentUtilities.getPartnerID(this.params)
        };
        super.setViewabilityTracking(resource, trackingObj);
        return resource;
    }
    setVastLoadTimeout(resource, timeout = undefined) {
        if (this.utils.Type.isEmpty(resource) ||
            this.utils.Type.isEmpty(timeout) ||
            !this.utils.Type.isNumber(timeout)) {
            this?.logger?.warn('setVastLoadTimeout', 'missing/invalid arguments... returning resource unmodified', 'timeout:', timeout);
            return resource;
        }
        resource = this.setCsaiParameter(resource, 'vastLoadTimeout', timeout);
        return resource;
    }
    setLoadVideoTimeoutMs(resource, timeout = undefined) {
        if (this.utils.Type.isEmpty(resource) ||
            this.utils.Type.isEmpty(timeout) ||
            !this.utils.Type.isNumber(timeout)) {
            this?.logger?.warn('setLoadVideoTimeoutMs', 'missing/invalid arguments... returning resource unmodified', 'timeout:', timeout);
            return resource;
        }
        resource = this.setCsaiParameter(resource, 'loadVideoTimeoutMs', timeout);
        return resource;
    }
    setAdCallUrl(resource, url) {
        if (this.utils.Type.isEmpty(resource) ||
            this.utils.Type.isEmpty(url) ||
            !this.utils.Type.isString(url)) {
            this?.logger?.warn('setAdCallUrl', 'missing/invalid arguments... returning resource unmodified', 'url:', url);
            return resource;
        }
        resource = this.setCsaiParameter(resource, 'adCallUrl', url);
        return resource;
    }
    setAdParameters(resource) {
        const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();
        if (adSetupInfo?.setup?.definition === AdSetupDefinition.NONE) {
            this?.logger?.log('setAdParameters:', 'adSetupInfo.setup.definition=NONE... no ad setup required... returning resource unmodified');
            return resource;
        }
        resource = this.setViewabilityTracking(resource);
        resource = this.setSsaiParameters(resource);
        this?.logger?.log('setAdParameters:', 'set resource ad parameters', resource);
        return resource;
    }
    setSsaiParameters(resource) {
        const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();
        if (adSetupInfo?.setup?.definition === AdSetupDefinition.DAI_SDK) {
            if (this.tag?.vodFatherUtilities?.isEnabled() &&
                this.utils.Type.isEmpty(this.tag?.vodFatherUtilities?.getDaiCmsId())) {
                this.logger?.warn('setSsaiParameters:', 'VODFather is enabled but daiCmsId is unset, contentSourceId cannot be set... removing ssai from resource');
                resource = this.removeSsai(resource);
                return resource;
            }
            resource = this.setDaiSdkAdParameters(resource);
            resource = this.setMillstoneDAIParams(resource);
            return resource;
        }
        if (adSetupInfo?.setup?.definition === AdSetupDefinition.LINEAR_AD_TRACKING) {
            return this.setLinearAdParameters(resource);
        }
        this.logger?.log('setSsaiParameters:', 'ad setup unknown/unsupported, exiting', adSetupInfo);
        return resource;
    }
    setDaiSdkAdParameters(resource) {
        const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();
        const daiSdkRequest = this.createFreewheelRequest(adSetupInfo);
        resource = this.setIsLive(resource, this.tag.model.contentUtilities.isLive(this.params, this?.tag?.model?.apiMetadata));
        resource = this.setAssetKey(resource, this.tag.model.contentUtilities.getDAIAssetKey(this.params, this?.tag?.model?.apiMetadata));
        resource = this.setFreewheelParameters(resource, daiSdkRequest);
        resource = this.setDaiSdkVodParameters(resource);
        resource = this.setEnableNonce(resource, this.tag.tagUtilities.isInternationalContext(this.tag));
        resource = this.setCustomUiOptions(resource);
        this?.logger?.log('setDaiSdkAdParameters:', 'set parameters required for DAI SDK ad setup', 'daiSdkRequest:', daiSdkRequest, 'resource:', resource);
        return resource;
    }
    setLinearAdParameters(resource) {
        resource = this.removeCsai(resource);
        if (!this.utils.Type.isEmpty(this.params?.AD?.FW?.FMS) &&
            this.utils.Type.isObject(this.params?.AD?.FW?.FMS)) {
            const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();
            const imaRequest = this.createFreewheelRequest(adSetupInfo);
            const adCallUrl = imaRequest.createAdCallUrl();
            resource = this.setAdCallUrl(resource, adCallUrl);
        }
        resource = this.setVastLoadTimeout(resource, PlayerLoadingTimeout.VAST);
        resource = this.setLoadVideoTimeoutMs(resource, PlayerLoadingTimeout.AD);
        this?.logger?.log('setLinearAdParameters', 'set parameters required for linear ad setup', 'resource:', resource);
        return resource;
    }
    setFreewheelParameters(resource, request) {
        if (this.utils.Type.isEmpty(request.parameters)) {
            this?.logger?.warn('setFreewheelParameters', 'Freewheel request has no parameters... exiting', 'request:', request, 'resource:', resource);
            return resource;
        }
        if (this.utils.Type.isEmpty(resource?.ad?.ssai)) {
            resource = this.initSsai(resource);
        }
        resource = this.initSsaiAdParameter(resource);
        request.parameters
            .filter((param) => {
            if (this.utils.Type.isEmpty(param.value)) {
                this?.logger?.log('setFreewheelParameters', 'filtering out param with unset value...', 'param:', param);
                return false;
            }
            return true;
        })
            .forEach((param) => {
            if (param.key === 'bundle' ||
                param.key === 'imafw_bundle') {
                resource = this.setCustParams(resource, `bundle=${param.value}`);
            }
            else {
                resource = this.setSsaiAdParameter(resource, param.key, param.value);
            }
        });
        this?.logger?.log('setFreewheelParameters', 'set Freewheel parameters', 'resource:', resource);
        return resource;
    }
    setDaiSdkVodParameters(resource) {
        if (this.TagConstants.DAI_SDK_VOD_ENABLED === false) {
            this.logger?.warn('setDaiSdkVodParameters:', 'DAI_SDK_VOD_ENABLED flag is disabled... exiting', resource);
            return resource;
        }
        if (this.tag.model.contentUtilities.isLive(this.params)) {
            this.logger?.warn('setDaiSdkVodParameters:', 'content is Live... exiting', resource);
            return resource;
        }
        resource = this.setContentSourceId(resource);
        resource = this.setVideoId(resource, this?.tag?.model?.apiMetadata?.contentId);
        this.setFormat(resource);
        this.logger?.log('setDaiSdkVodParameters:', 'set parameters required for VOD playback via DAI SDK', resource);
        return resource;
    }
    setContentSourceId(resource) {
        if (this.tag?.vodFatherUtilities?.isEnabled()) {
            const daiCmsId = this.tag?.vodFatherUtilities?.getDaiCmsId();
            this.logger?.log('setContentSourceId', 'VODFather is enabled, setting contentSourceId to daiCmsId:', daiCmsId);
            return super.setContentSourceId(resource, daiCmsId);
        }
        this.logger?.log('setContentSourceId', 'VODFather is disabled, setting contentSourceId to AdOps constant:', this.TagConstants?.ADOPS_ID?.CMSID);
        return super.setContentSourceId(resource, this.TagConstants?.ADOPS_ID?.CMSID);
    }
    setCustomUiOptions(resource) {
        if (!resource.ad?.ssai) {
            this.logger?.warn('setCustomUiOptions:', 'SSAI configuration is missing in resource... exiting');
            return resource;
        }
        super.setCustomUiOptions(resource, {
            enabled: true,
            skippableSupport: true,
            clickThroughNavigation: ClickThroughNavigation.EXTERNAL
        });
        return resource;
    }
    setFormat(resource) {
        switch (this?.tag?.model?.apiMetadata?.assetType) {
            case MPXAssetType.DASH_CBCS_CMAF:
            case MPXAssetType.DASH_CENC:
            case MPXAssetType.DASH_CENC_HDR:
            case MPXAssetType.DASH_CENC_HDR10:
            case MPXAssetType.DASH_CENC_LGCY:
            case MPXAssetType.DASH_CENC_PRECON:
            case MPXAssetType.DASH_CMAF:
            case MPXAssetType.DASH_LIVE:
            case MPXAssetType.DASH_TA:
                resource = super.setFormat(resource, PlayerAdapterVideoFormat.DASH);
                break;
            default:
                this?.logger?.warn('setFormat:', 'invalid/unsupported apiMetadata assetType, could not set resource.ad.ssai.format', 'assetType:', this?.tag?.model?.apiMetadata?.assetType);
                break;
        }
        return resource;
    }
}
